import { CanvasHandler } from "../../../handlers";
import { getSvgObject } from "../../../utils/ObjectUtil";
import store from "../../../redux/store";
import { RowPropertiesEnum } from "../../../enums/RowPropertiesEnum";
import { RowLabelHelper, RowSeatLabelHelper } from "../helpers";
import { DirectionEnum, ObjectTypesEnum } from "../../../enums";
import { RowSeatHelper } from "../helpers/RowSeatHelper";
import { Icons } from '../../../enums/SeatIcon'

class RowProperties {
  private LABEL_OFFSET = 15;
  public canvasHandler: CanvasHandler;
  public handlers: any;

  constructor(canvasHandler: CanvasHandler
  ) {
    this.canvasHandler = canvasHandler;
    this.handlers = {
      [RowPropertiesEnum.Accesible]: this.updateIcon,
      [RowPropertiesEnum.Companion]: this.updateIcon,
      [RowPropertiesEnum.RestrictedView]: this.updateIcon,
      [RowPropertiesEnum.DisplayedRowLabel]: this.updateDisplayedRowLabel,
      [RowPropertiesEnum.DisplayedSeatLabel]: this.updateDisplayedSeatLabel,
      [RowPropertiesEnum.DisplayedSectionLabel]: this.updateDisplayedSectionLabel,
      [RowPropertiesEnum.EnabledRowLabel]: this.updateEnabledRowLabel,
      [RowPropertiesEnum.LabelPositionLeft]: this.updateLabelPosition,
      [RowPropertiesEnum.LabelPositionRight]: this.updateLabelPosition,
      [RowPropertiesEnum.SectionLabel]: this.updateSectionLabel,
      [RowPropertiesEnum.Reverse]: this.updateReverse,
      [RowPropertiesEnum.RowCategory]: this.updateRowCategory,
      [RowPropertiesEnum.RowLabel]: this.updateRowLabel,
      [RowPropertiesEnum.SeatCategory]: this.updateSeatCategory,
      [RowPropertiesEnum.SeatLabel]: this.updateSeatLabel,
      [RowPropertiesEnum.SeatLabeling]: this.updateSeatLabeling,
      [RowPropertiesEnum.SeatsCurve]: this.updateSeatsCurve,
      [RowPropertiesEnum.SeatsQuantity]: this.updateSeatsQuantity,
      [RowPropertiesEnum.SeatsSpacing]: this.updateSeatsSpacing,
      [RowPropertiesEnum.StartsAt]: this.updateStartsAt
    };
  }

  public updateProperties = (
    selectedItem: any,
    key: RowPropertiesEnum,
    value: any,
    allValues: any,
  ) => {
    const state = store.getState();
    const {
      properties: { rowSeatSize, categories: categoriesList },
    } = state;
    const objects: any = selectedItem.getObjects();
    const seats: any = selectedItem.getObjects(ObjectTypesEnum.Group);
    const textObjs = selectedItem.getObjects(ObjectTypesEnum.TextBox) || [];
    const textRight =
      textObjs.filter((textbox: any) => textbox.side === DirectionEnum.Right) || [];
    const textLeft =
      textObjs.filter((textbox: any) => textbox.side === DirectionEnum.Left) || [];
    const renderedIcon = selectedItem.getObjects(ObjectTypesEnum.Svg) || undefined;
    const seatsQuantity = seats.length;
    const old = { ...selectedItem };
    const selectedCategory: any =
      key === RowPropertiesEnum.RowCategory || key === RowPropertiesEnum.SeatCategory
        ? categoriesList.find((category: any) => category.key === value)
        : "";
    const handler = this.handlers[key];
    handler.call(this, {
      rowSeatSize,
      objects,
      seats,
      textRight,
      textLeft,
      renderedIcon,
      seatsQuantity,
      old,
      selectedCategory,
      selectedItem,
      value,
      key,
    });
  };

  private setSeatValue = (seat: any, rowData: any, seatlabel: string) => {
    seat.set({
      value: `${rowData.section.label}${rowData.section.label &&
        rowData.row.label &&
        "-"}${rowData.row.label}${(rowData.row.label ||
          rowData.section.label) &&
        seatlabel &&
        "-"}${seatlabel}`,
      isLabeled:
        (rowData.row.enabled ? rowData.row.label !== "" : true) &&
        seatlabel !== "",
    });
  };

  private updateRowCategory({
    selectedItem,
    seatsQuantity,
    seats,
    selectedCategory,
  }: any) {
    const { color, name } = selectedCategory;
    selectedItem.set({
      category: { ...selectedCategory },
    });
    if (!selectedItem.zoneID) {
      selectedItem.set({
        section: {
          label: name,
          displayedLabel: name,
        },
      });
    }
    for (let index = 0; index < seatsQuantity; index++) {
      seats[index].set({
        category: { ...selectedCategory },
      });
      this.setSeatValue(seats[index], selectedItem, seats[index].label);

      const seatCircle = seats[index].item(0);
      seatCircle.set({
        stroke: color,
        fill: `${color}40`,
      });
    }
    selectedItem.addWithUpdate();
  }

  private async updateSeatsQuantity({
    selectedItem,
    value,
    rowSeatSize,
    textRight,
    textLeft,
    seats,
  }: any) {
    await RowSeatHelper.updateSeatsQuantity({
      selectedItem,
      value,
      rowSeatSize,
      textRight,
      textLeft,
      seats,
      setSeatValue: this.setSeatValue,
      editable: this.canvasHandler.editable,
      container: this.canvasHandler.container.id,
      objectOption: this.canvasHandler.objectOption,
      labelOffset: this.LABEL_OFFSET,
    });
  }

  private updateSeatsCurve({
    selectedItem,
    value,
    seats,
    textRight,
    textLeft,
    rowSeatSize,
  }: any) {
    RowSeatHelper.updateSeatsCurve({
      selectedItem,
      value,
      seats,
      rowSeatSize,
      textRight,
      textLeft,
      labelOffset: this.LABEL_OFFSET,
    });
  }

  private updateSeatsSpacing({
    selectedItem,
    value,
    seats,
    rowSeatSize,
    textRight,
    textLeft
  }: any) {
    RowSeatHelper.updateSeatsSpacing({
      selectedItem,
      value,
      seats,
      rowSeatSize,
      textRight,
      textLeft,
      labelOffset: this.LABEL_OFFSET,
    });
  }

  private updateSeatLabeling({ selectedItem, value, seats }: any) {
    RowSeatLabelHelper.applySeatDistribution({
      selectedItem,
      seats: seats,
      distributionKey: value,
      startsAt: 1,
      reverse: false,
      setSeatValue: this.setSeatValue,
      distributionObject: 'seat'
    });

    selectedItem.addWithUpdate();
  }

  private updateStartsAt({ selectedItem, value, seats }: any) {
    const {seat: {reverse, key}} = selectedItem;
    RowSeatLabelHelper.applySeatDistribution({
      selectedItem,
      seats,
      distributionKey: key,
      startsAt: value,
      reverse,
      setSeatValue: this.setSeatValue,
      distributionObject: 'seat'
    });
  }

  private updateReverse({ selectedItem, value, seats }: any) {
    const {seat: {startsAt, key}} = selectedItem;
    RowSeatLabelHelper.applySeatDistribution({
      selectedItem,
      seats,
      distributionKey: key,
      startsAt,
      reverse: value,
      setSeatValue: this.setSeatValue,
      distributionObject: 'seat'
    });
  }

  private updateSectionLabel({
    selectedItem,
    value,
    seatsQuantity,
    objects,
  }: any) {
    selectedItem.set({
      section: {
        label: value,
        displayedLabel: selectedItem.section.displayedLabel,
      },
    });

    for (let index = 0; index < seatsQuantity; index++) {
      this.setSeatValue(objects[index], selectedItem, objects[index].label);
      objects[index].set({
        sectionValue: value
      })
    }

    selectedItem.addWithUpdate();
  }

  private updateDisplayedSectionLabel({ selectedItem, value }: any) {
    selectedItem.set({
      section: {
        label: selectedItem.section.label,
        displayedLabel: value,
      },
    });
    selectedItem.addWithUpdate();
  }

  private updateEnabledRowLabel({
    selectedItem,
    value,
    seatsQuantity,
    objects,
    textRight,
    textLeft,
  }: any) {
    selectedItem.set({
      row: {
        label: "",
        displayedLabel: "",
        enabled: value,
        position: {
          left: false,
          right: false,
        },
      },
    });
    for (let index = 0; index < seatsQuantity; index++) {
      this.setSeatValue(objects[index], selectedItem, objects[index].label);
    }

    if (textRight.length > 0 && !value) {
      RowLabelHelper.removeRowLabel(selectedItem, DirectionEnum.Right);
    }
    if (textLeft.length > 0 && !value) {
      RowLabelHelper.removeRowLabel(selectedItem, DirectionEnum.Left);
    }
    RowLabelHelper.updateBounds(selectedItem);
  }

  private updateRowLabel({
    selectedItem,
    value,
    seatsQuantity,
    rowSeatSize,
    objects,
    textRight,
    textLeft,
  }: any) {

    selectedItem.set({
      row: {
        ...selectedItem.row,
        label: value,
      },
    });

    for (let i = 0; i < seatsQuantity; i++) {
      this.setSeatValue(objects[i], selectedItem, objects[i].label);
      objects[i].set({
        rowLabel: value
      });
    }

    if (!value) return;

    RowSeatHelper.refreshRowLabels({selectedItem, rowSeatSize, textLeft, textRight, labelOffset: this.LABEL_OFFSET});

    RowLabelHelper.updateBounds(selectedItem);
  }

  private updateDisplayedRowLabel({
    selectedItem,
    value,
    rowSeatSize,
    textRight,
    textLeft,
    seats,
  }: any) {
    selectedItem.set({
      row: {
        ...selectedItem.row,
        displayedLabel: value,
      },
    });

    if (!seats?.length) return;

    RowSeatHelper.refreshRowLabels({selectedItem, rowSeatSize, textLeft, textRight, labelOffset: this.LABEL_OFFSET});

    RowLabelHelper.updateBounds(selectedItem);
  }

  private updateLabelPosition({
    selectedItem,
    value,
    key,
    rowSeatSize,
  }: any) {
    const side = key === RowPropertiesEnum.LabelPositionLeft ? DirectionEnum.Left : DirectionEnum.Right;

    selectedItem.set({
      row: {
        label: selectedItem.row.label,
        displayedLabel: selectedItem.row.displayedLabel,
        enabled: selectedItem.row.enabled,
        position: {
          left:
            key === RowPropertiesEnum.LabelPositionLeft
              ? value
              : selectedItem.row.position.left,
          right:
            key === RowPropertiesEnum.LabelPositionRight
              ? value
              : selectedItem.row.position.right,
        },
      },
    });

    const {
      row: { displayedLabel: displayed, label: labl },
    } = selectedItem;

    const selectedText =
      displayed !== "" ? displayed : labl !== "" ? labl : "?";

    if (!value) {
      RowLabelHelper.removeRowLabel(selectedItem, side);

      selectedItem.setCoords();
      return;
    }

    const label = RowLabelHelper.createRowLabel({
      selectedItem,
      rowSeatSize,
      text: selectedText,
      side,
      labelOffset: this.LABEL_OFFSET,
      angle: selectedItem.angleHelp,
    });

    if (!label) return;

    RowLabelHelper.addRowLabel(selectedItem, label);

    selectedItem.setCoords();
  }

  private updateSeatCategory({ selectedItem, selectedCategory }: any) {
    selectedItem.set({
      category: {
        ...selectedCategory,
      },
    });
    const seatCircle = selectedItem.item(0);
    seatCircle.set({
      stroke: selectedCategory.color,
      fill: `${selectedCategory.color}40`,
    });
    selectedItem.group.addWithUpdate();
  }

  private updateSeatLabel({ selectedItem, value }: any) {
    if (selectedItem.displayedLabel === "") {
      selectedItem._objects[1].set({
        text: value,
      });
    }
    selectedItem.set({
      label: value,
    });

    this.setSeatValue(selectedItem, selectedItem.group, value);
  }

  private updateDisplayedSeatLabel({ selectedItem, value }: any) {
    if (value === "") {
      selectedItem._objects[1].set({
        text: selectedItem.label,
      });
    } else {
      selectedItem._objects[1].set({
        text: value,
      });
    }
    selectedItem.set({
      displayedLabel: value,
    });
  }

  private updateIcon({
    selectedItem,
    value,
    rowSeatSize,
    renderedIcon,
    key,
  }: any) {
    selectedItem.set({
      [key]: value,
    });

    if (key === RowPropertiesEnum.Companion && value && selectedItem.companion) {
      selectedItem.set({
        companion: false,
      });
    }

    if (key === RowPropertiesEnum.Accesible && value && selectedItem.accesible) {
      selectedItem.set({
        accesible: false,
      });
    }

    if (renderedIcon?.[1]) {
      selectedItem.remove(renderedIcon[1]);
    }

    let iconName: Icons | undefined;

    if (selectedItem.restrictedView) {
      iconName = Icons.restricted;
    } else if (selectedItem.companion) {
      iconName = Icons.companion;
    } else if (selectedItem.accesible) {
      iconName = Icons.accesible;
    }

    if (iconName) {
      selectedItem.insertAt(
        getSvgObject(iconName, rowSeatSize, "#000000"),
        3,
        true
      );

      if (selectedItem._objects[1]) {
        selectedItem._objects[1].set({ visible: false });
      }
    } else {
      if (selectedItem._objects[1]) {
        selectedItem._objects[1].set({ visible: true });
      }
    }

    selectedItem.group.addWithUpdate();
  }

}

export default RowProperties;
